import { useMemo } from "react";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { DollarSign, CalendarDays, Wallet, RotateCcw, AlertTriangle, TrendingUp } from "lucide-react";
import { KpiCard, PageHeader, Loading } from "@/components/portal/widgets";
import { ChartPanel, tooltipStyle, ExportButton, CompanyLink, ErrorNote } from "@/components/founder/ui";
import { useFounder, money, fmtD, fmtMonth } from "@/lib/founderApi";

export default function FounderRevenue() {
  const { demo } = useOutletContext();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") || "";
  const { data: o, error } = useFounder(`/platform/overview?include_demo=${demo}`);
  const { data: pay } = useFounder(`/platform/payments?include_demo=${demo}`);
  const { data: companies } = useFounder(`/platform/companies?include_demo=${demo}`);

  const mrrByPlan = useMemo(() => ["starter", "growth", "fleet", "enterprise"].map((p) => ({
    plan: p, mrr: (companies || []).filter((c) => c.plan === p).reduce((s, c) => s + (c.mrr || 0), 0),
    count: (companies || []).filter((c) => c.plan === p).length,
  })), [companies]);
  const payments = (pay?.payments || []).filter((p) => !month || (p.created_at || p.date || "").startsWith(month));
  const potential = (companies || []).filter((c) => c.status === "trial").reduce((s, c) => s + (c.plan_price || 0), 0);

  if (error) return <ErrorNote error={error} />;
  if (!o) return <Loading label="Loading earnings…" />;
  const r = o.revenue || {};

  return (
    <div className="space-y-8" data-testid="founder-revenue">
      <PageHeader title="Earnings" subtitle={`Payments source: ${pay?.source || o.payments_source}. Tap a month or plan to drill down.`}>
        <ExportButton kind="payments" demo={demo} label="Export payments" />
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={TrendingUp} label="MRR" accent="emerald" value={money(o.mrr)} sub={`${money(o.mrr * 12)} a year at this rate`} testid="rev-kpi-mrr" to="/founder/companies?sort=mrr" />
        <KpiCard icon={CalendarDays} label="This month" accent="emerald" value={money(r.this_month)} sub={`${money(r.last_month)} last month`} testid="rev-kpi-month" />
        <KpiCard icon={Wallet} label="All-time" value={money(r.all_time)} sub={`${money(r.last_30)} in the last 30 days`} testid="rev-kpi-all" />
        <KpiCard icon={DollarSign} label="Trial pipeline" accent="amber" value={money(potential)} sub="Monthly value if every trial converts" testid="rev-kpi-pipeline" to="/founder/companies?status=trial" />
        <KpiCard icon={RotateCcw} label="Refunds" accent="slate" value={money(r.refunds)} testid="rev-kpi-refunds" />
        <KpiCard icon={AlertTriangle} label="Failed (30d)" accent="red" value={r.failed_30 || 0} sub="Payment attempts that failed" testid="rev-kpi-failed" to="/founder/companies?status=past_due" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartPanel title="Earned by month" hint="Tap a bar to filter the payments below" testid="rev-chart-monthly">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={o.revenue_monthly} onClick={(e) => e?.activeLabel && setParams({ month: e.activeLabel })} style={{ cursor: "pointer" }}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${v}`} width={50} />
              <Tooltip {...tooltipStyle} labelFormatter={fmtMonth} formatter={(v) => [money(v), "Earned"]} />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]} minPointSize={3}>
                {(o.revenue_monthly || []).map((m) => <Cell key={m.month} fill={m.month === month ? "#10b981" : "#34d399"} fillOpacity={!month || m.month === month ? 1 : 0.35} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="MRR by plan" hint="Tap a plan to see its companies" testid="rev-chart-plan">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={mrrByPlan} layout="vertical">
              <CartesianGrid stroke="#1e293b" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <YAxis type="category" dataKey="plan" stroke="#94a3b8" fontSize={12} width={80} />
              <Tooltip {...tooltipStyle} formatter={(v, n, p) => [`${money(v)} · ${p.payload.count} companies`, "MRR"]} />
              <Bar dataKey="mrr" fill="#3b82f6" radius={[0, 4, 4, 0]} cursor="pointer" minPointSize={3} onClick={(d) => nav(`/founder/companies?plan=${d.plan || d.payload?.plan}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>
      <ChartPanel title={month ? `Payments · ${fmtMonth(month)}` : "All payments"} testid="rev-payments"
        action={month && <button onClick={() => setParams({})} className="text-xs font-semibold text-blue-300" data-testid="rev-month-clear">Show all months</button>}>
        {!pay ? <Loading label="Loading payments…" /> : !payments.length ? <p className="text-sm text-slate-500" data-testid="rev-payments-empty">No payments recorded{month ? " in this month" : " yet"} — companies are still on their free trial.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-left text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="py-2">Date</th><th>Company</th><th>Description</th><th>Status</th><th className="text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-800">
                {payments.map((p, i) => (
                  <tr key={p.id || i} data-testid={`rev-payment-${i}`}>
                    <td className="py-2 text-slate-400">{fmtD(p.created_at || p.date)}</td>
                    <td><CompanyLink id={p.org_id} name={p.org_name || p.company} /></td>
                    <td className="text-slate-400">{p.description || p.plan || "—"}</td>
                    <td className={`capitalize ${p.status === "failed" ? "text-red-300" : "text-slate-300"}`}>{p.status}</td>
                    <td className="text-right font-mono-data text-emerald-300">{money(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartPanel>
    </div>
  );
}
